import type { FFmpeg, ProgressEventCallback } from '@ffmpeg/ffmpeg';
import { getImgCloudBasename } from '../utils/appRouting';

export const MAX_AUDIO_OUTPUT_BYTES = 150 * 1024 * 1024;
export const AUDIO_BITRATES = [32, 48, 64, 96, 128, 160, 192, 256, 320] as const;
export type AudioExportStage = 'loading' | 'analyzing' | 'compressing' | 'finalizing';
export interface AudioMetadata { duration: number | null; channels: number; sampleRate: number }
export interface Mp3Metadata extends AudioMetadata { duration: number }
export interface AudioExportUpdate<T extends AudioMetadata> { stage: AudioExportStage; progress: number; metadata?: T }
export interface AudioExportResult { blob: Blob; metadata: Mp3Metadata }

export class AudioExportError extends Error {
  constructor(public readonly code: string) { super(code); }
}

export const estimateMp3Bytes = (duration: number, bitrate: number): number =>
  Math.ceil(duration * bitrate * 1000 / 8);

export const parseMp3Metadata = (json: string): Mp3Metadata => {
  try {
    const data = JSON.parse(json);
    const stream = data.streams?.find((entry: { codec_type?: string }) => entry.codec_type === 'audio') || data.streams?.[0];
    const duration = Number(data.format?.duration ?? stream?.duration);
    const channels = Number(stream?.channels);
    const sampleRate = Number(stream?.sample_rate);
    if (data.format?.format_name !== 'mp3' || stream?.codec_name !== 'mp3'
        || !Number.isFinite(duration) || duration <= 0
        || ![1, 2].includes(channels) || !Number.isFinite(sampleRate) || sampleRate <= 0) {
      throw new Error('Invalid MP3 stream');
    }
    return { duration, channels, sampleRate };
  } catch {
    throw new AudioExportError('invalidAudio');
  }
};

const resolveCoreUrls = async (signal: AbortSignal) => {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const configuredBases = publicUrl ? [`${publicUrl}/ffmpeg-core`] : [];
  const basename = getImgCloudBasename();
  const resolveAsset = async (fileName: string, bases: string[]): Promise<string> => {
    for (const base of Array.from(new Set(bases))) {
      const url = new URL(`${base}/${fileName}`, window.location.href).href;
      try {
        const response = await fetch(url, { method: 'HEAD', signal });
        if (response.ok && !response.headers.get('content-type')?.includes('text/html')) return url;
      } catch {
        if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
      }
    }
    throw new AudioExportError('engineFailed');
  };
  // Nginx adds /img-cloud to JS requests, while WASM uses its physical path.
  // Resolve them independently so successful assets never trigger fallback probes.
  const [coreURL, wasmURL] = await Promise.all([
    resolveAsset('ffmpeg-core.js', [...configuredBases, '/ffmpeg-core', `${basename}/ffmpeg-core`, '/img-cloud/ffmpeg-core']),
    resolveAsset('ffmpeg-core.wasm', [...configuredBases, '/img-cloud/ffmpeg-core', `${basename}/ffmpeg-core`, '/ffmpeg-core']),
  ]);
  return { coreURL, wasmURL };
};

export interface AudioExportSource<T extends AudioMetadata> {
  validateFile: (file: File) => void;
  inputName: (file: File) => string;
  parseMetadata: (json: string) => T;
  buildArgs: (bitrate: number, metadata: T, inputPath: string) => string[];
}

export class AudioExportJob<T extends AudioMetadata> {
  constructor(private readonly source: AudioExportSource<T>) {}
  private readonly abortController = new AbortController();
  private ffmpeg: FFmpeg | null = null;

  cancel = (): void => {
    this.abortController.abort();
    this.ffmpeg?.terminate();
    this.ffmpeg = null;
  };

  private checkActive = (): void => {
    if (this.abortController.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
  };

  async run(file: File, bitrate: number, onUpdate: (update: AudioExportUpdate<T>) => void): Promise<AudioExportResult> {
    this.source.validateFile(file);
    if (!(AUDIO_BITRATES as readonly number[]).includes(bitrate)) throw new AudioExportError('invalidBitrate');
    const inputName = this.source.inputName(file);
    const inputPath = `/input/${inputName}`;
    this.checkActive();
    if (typeof Worker === 'undefined' || typeof WebAssembly === 'undefined') {
      throw new AudioExportError('unsupported');
    }
    let stage: AudioExportStage = 'loading';
    const update = (progress: number, metadata?: T) => {
      this.checkActive();
      onUpdate({ stage, progress, metadata });
    };
    let mounted = false;
    let progressHandler: ProgressEventCallback | undefined;
    let runtime: FFmpeg | null = null;
    try {
      update(0);
      const { FFmpeg: FFmpegCtor, FFFSType } = await import('@ffmpeg/ffmpeg');
      this.checkActive();
      runtime = new FFmpegCtor();
      this.ffmpeg = runtime;
      const urls = await resolveCoreUrls(this.abortController.signal);
      this.checkActive();
      await runtime.load(urls, { signal: this.abortController.signal });
      this.checkActive();
      stage = 'analyzing';
      update(0);
      await runtime.createDir('/input');
      this.checkActive();
      // WORKERFS reads slices of the File; no full input copy or decoded PCM buffer.
      mounted = await runtime.mount(FFFSType.WORKERFS, {
        blobs: [{ name: inputName, data: file }],
      }, '/input');
      this.checkActive();
      if (!mounted) throw new AudioExportError('engineFailed');
      const probe = async (path: string, output: string): Promise<string> => {
        const code = await runtime!.ffprobe([
          '-v', 'error',
          '-show_entries', 'format=format_name,duration:stream=codec_type,codec_name,duration,channels,sample_rate:stream_disposition=attached_pic',
          '-of', 'json', path, '-o', output,
        ]);
        this.checkActive();
        // The bundled 0.12.10 core leaves ffprobe's status at -1 even on success.
        // Accept that status only when a fresh report can be parsed and validated.
        if (code !== 0 && code !== -1) throw new AudioExportError('invalidAudio');
        let data;
        try {
          data = await runtime!.readFile(output, 'utf8');
        } catch {
          this.checkActive();
          throw new AudioExportError('invalidAudio');
        }
        this.checkActive();
        return String(data);
      };
      const metadata = this.source.parseMetadata(await probe(inputPath, '/probe.json'));
      const args = this.source.buildArgs(bitrate, metadata, inputPath);
      update(0, metadata);
      if (metadata.duration !== null && estimateMp3Bytes(metadata.duration, bitrate) > MAX_AUDIO_OUTPUT_BYTES) {
        throw new AudioExportError('outputTooLarge');
      }
      stage = 'compressing';
      update(0, metadata);
      let lastProgress = 0;
      progressHandler = ({ time }) => {
        if (this.abortController.signal.aborted || !metadata.duration) return;
        const progress = Math.min(99, Math.max(0, Math.floor(time / 1000000 / metadata.duration * 100)));
        if (Number.isFinite(progress) && progress > lastProgress) {
          lastProgress = progress;
          update(progress, metadata);
        }
      };
      runtime.on('progress', progressHandler);
      const code = await runtime.exec(args);
      this.checkActive();
      if (code !== 0) throw new AudioExportError('compressionFailed');
      runtime.off('progress', progressHandler);
      stage = 'finalizing';
      update(99, metadata);
      const outputMetadata = parseMp3Metadata(await probe('/output.mp3', '/output-probe.json'));
      if (metadata.duration !== null && Math.abs(outputMetadata.duration - metadata.duration) > Math.max(0.25, metadata.duration * 0.001)) {
        throw new AudioExportError('incompleteOutput');
      }
      const data = await runtime.readFile('/output.mp3');
      this.checkActive();
      if (!(data instanceof Uint8Array) || data.byteLength === 0) {
        throw new AudioExportError('compressionFailed');
      }
      if (data.byteLength >= MAX_AUDIO_OUTPUT_BYTES) throw new AudioExportError('outputTooLarge');
      return { blob: new Blob([data], { type: 'audio/mpeg' }), metadata: outputMetadata };
    } catch (error) {
      this.checkActive();
      if (error instanceof AudioExportError) throw error;
      const detail = String(error);
      if (/memory|allocation|out of bounds/i.test(detail)) throw new AudioExportError('outOfMemory');
      throw new AudioExportError(stage === 'loading' ? 'engineFailed' : 'compressionFailed');
    } finally {
      if (runtime) {
        if (progressHandler) runtime.off('progress', progressHandler);
        try {
          if (runtime.loaded && !this.abortController.signal.aborted) {
            if (mounted) await runtime.unmount('/input');
            await Promise.allSettled(['/output.mp3', '/probe.json', '/output-probe.json'].map(
              (path) => runtime!.deleteFile(path),
            ));
          }
        } catch {
          // Cancellation may terminate the worker during cleanup.
        } finally {
          runtime.terminate();
          if (this.ffmpeg === runtime) this.ffmpeg = null;
        }
      }
    }
  }
}
